import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Check, Loader2, Settings, CalendarDays, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { decodeJwtPayload, getAuthSession, saveAuthSession } from "@/lib/auth-session";
import { clearBootstrapCache } from "@/lib/bootstrap-cache";
import { API_BASE } from "@/lib/utils";

function displayInitials(email, name) {
  const n = String(name || "").trim();
  if (n.length >= 2) return n.slice(0, 2).toUpperCase();
  const local = String(email || "").split("@")[0] || "?";
  return local.slice(0, 2).toUpperCase();
}

function displayName(email, rosterName) {
  const n = String(rosterName || "").trim();
  if (n) return n;
  const local = String(email || "").split("@")[0] || "Operator";
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export default function Profile() {
  const session = getAuthSession();
  const jwtTenant = useMemo(() => {
    const payload = decodeJwtPayload(session?.token);
    return String(payload?.tenant || session?.tenant || "").trim();
  }, [session?.token, session?.tenant]);

  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState("");
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/memberships`, { headers: getAuthHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Failed to load workspaces (${res.status})`);
      setPayload(body);
    } catch (err) {
      toast.error("Could not load workspaces", { description: err?.message || "Try again shortly." });
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const email = String(payload?.email || session?.email || "").trim();
  const currentTenantId = String(payload?.currentTenantId || jwtTenant || "").trim();
  const memberships = Array.isArray(payload?.memberships) ? payload.memberships : [];
  const otherWorkspaces = memberships.filter((m) => m.tenantId !== currentTenantId);
  const currentWorkspace = memberships.find((m) => m.tenantId === currentTenantId);

  const switchWorkspace = async (tenantId, managePath) => {
    if (!tenantId || tenantId === currentTenantId) {
      if (managePath) window.location.assign(managePath);
      return;
    }
    setSwitching(tenantId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/switch-workspace`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "content-type": "application/json" },
        body: JSON.stringify({ tenantId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Switch failed (${res.status})`);
      if (!body?.token) throw new Error("No session token returned");

      clearBootstrapCache();
      saveAuthSession({
        email: body.email || email,
        token: body.token,
        role: body.role || "admin",
        tenant: body.tenant || tenantId,
        via: session?.via || "workspace-switch",
      });
      toast.success("Workspace switched", {
        description: body.tenant === currentTenantId ? undefined : "Reloading admin…",
      });
      const dest = managePath && managePath.startsWith("/") ? managePath : "/admin";
      window.location.assign(dest);
    } catch (err) {
      toast.error("Could not switch workspace", { description: err?.message || "Try again." });
      setSwitching("");
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[900px] mx-auto" data-testid="profile-page">
      <div className="text-overline">Account</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Profile & workspaces</h1>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-xl">
        Your operator identity and workspaces you can access. Switching reloads the admin so branding and data match the
        selected account.
      </p>

      <div className="mt-10 border border-[var(--border-default)] p-6 bg-[var(--bg-surface)]">
        <div className="flex items-start gap-4">
          <Avatar className="w-14 h-14 rounded-none border border-[var(--border-default)]">
            <AvatarFallback className="rounded-none bg-[var(--primary)] text-[var(--on-primary)] text-sm font-medium">
              {displayInitials(email)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-heading text-xl">{displayName(email)}</div>
            <div className="text-sm font-mono text-[var(--text-secondary)] mt-1">{email || "—"}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-2 font-mono uppercase tracking-widest">
              Role · {String(session?.role || "admin")}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-overline">Current workspace</h2>
        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <Loader2 size={16} className="animate-spin" /> Loading workspaces…
          </div>
        ) : (
          <div className="mt-4 border border-[var(--border-default)] p-5 bg-[var(--bg-surface)]">
            <div className="flex items-start gap-3">
              <Building2 size={20} className="text-[var(--primary)] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-lg">
                  {currentWorkspace?.workspaceName || currentTenantId || "Workspace"}
                </div>
                <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{currentTenantId || "—"}</div>
                {currentWorkspace?.role ? (
                  <div className="text-xs text-[var(--text-secondary)] mt-2">Your role · {currentWorkspace.role}</div>
                ) : null}
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
                <Check size={12} /> Active
              </span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-none text-xs font-mono uppercase tracking-widest" asChild>
                <Link to="/admin/settings">
                  <Settings size={14} className="mr-2" /> Workspace settings
                </Link>
              </Button>
              <Button variant="outline" className="rounded-none text-xs font-mono uppercase tracking-widest" asChild>
                <Link to="/admin/events">
                  <CalendarDays size={14} className="mr-2" /> Events
                </Link>
              </Button>
              <Button variant="outline" className="rounded-none text-xs font-mono uppercase tracking-widest" asChild>
                <Link to="/admin/billing">
                  <CreditCard size={14} className="mr-2" /> Billing
                </Link>
              </Button>
            </div>
          </div>
        )}
      </section>

      {!loading && otherWorkspaces.length > 0 ? (
        <section className="mt-10">
          <h2 className="text-overline">Other workspaces</h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            You belong to {memberships.length} workspaces. Switch to manage another account with your same sign-in.
          </p>
          <ul className="mt-4 space-y-3">
            {otherWorkspaces.map((ws) => (
              <li
                key={ws.tenantId}
                className="border border-[var(--border-default)] p-5 bg-[var(--bg-surface)] flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg">{ws.workspaceName || ws.tenantId}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{ws.tenantId}</div>
                  {ws.role ? <div className="text-xs text-[var(--text-secondary)] mt-2">Role · {ws.role}</div> : null}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    className="rounded-none text-xs font-mono uppercase tracking-widest"
                    disabled={Boolean(switching)}
                    onClick={() => switchWorkspace(ws.tenantId)}
                  >
                    {switching === ws.tenantId ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" /> Switching…
                      </>
                    ) : (
                      "Switch to workspace"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-none text-xs font-mono uppercase tracking-widest"
                    disabled={Boolean(switching)}
                    onClick={() => switchWorkspace(ws.tenantId, "/admin/settings")}
                  >
                    Manage settings
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!loading && memberships.length <= 1 ? (
        <p className="mt-8 text-sm text-[var(--text-tertiary)]">
          This account is only linked to one workspace. Team invites for additional workspaces will appear here when
          supported.
        </p>
      ) : null}
    </div>
  );
}
